import React, { useEffect, useMemo, useState } from "react";
import { View, Text, Pressable, ScrollView, StyleSheet } from "react-native";
import { MotiView } from "moti";
import AdminLayout from "../../layouts/AdminLayout.web";
import { colors, spacing, radii, typographyScale, typographyStyles, shadowStyles } from "../../styles/design-system";

import { Robot, ArrowsClockwise } from "@phosphor-icons/react";
import { RobotIcon } from "@phosphor-icons/react";
import { ArrowsClockwiseIcon } from "@phosphor-icons/react";
import { useGetCourseSessionAnalyticsQuery, CourseSessionAnalyticsInput } from "../../graphql/generated/graphql";
import CommonQuestionsTab from "../../components/dashboard/Tabs/CommonQuestionsTab";
import AssignmentSupportTab from "../../components/dashboard/Tabs/AssignmentSupportTab";
import FacultyStatsCards from "../../components/dashboard/Faculty/FacultyStatsCards";

import {
  DataTable,
  CellTextBold,
  Column,
  CellText,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../components/adminDashboard/AICompanion";
import { useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { formatTimeAgo } from "../../utils/date";

type DashboardTab = "common" | "assignment" | "activity";

function FacultyDashboardCompanionView() {
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshRotation, setRefreshRotation] = useState(0);

  const [selectedTab, setSelectedTab] = useState<DashboardTab>("activity");
  const [commonQuestionCount, setCommonQuestionCount] = useState(0);
  const [assignmentCount, setAssignmentCount] = useState(0);

  const { userInfo } = useContext(AuthContext);
  const facultyId = userInfo?.userId;

  const { data: courseAnalyticsData, refetch: refetchCourseAnalytics } = useGetCourseSessionAnalyticsQuery({
    variables: {
      input: {
        startDate: undefined,
        endDate: undefined,
        limit: 5,
        facultyId: facultyId,
      } as CourseSessionAnalyticsInput,
    },
    skip: !facultyId,
  });

  const handleTabChange = (value: string) => {
    if (value === "common" || value === "assignment" || value === "activity") {
      setSelectedTab(value);
    }
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    setRefreshRotation((prev) => prev + 360);

    try {
      await refetchCourseAnalytics();
      setLastUpdated(new Date()); // update timestamp
    } catch (error) {
      console.error("Error refreshing faculty analytics:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  type ActiveStudent = {
    id: string;
    name: string;
    initials: string;
    topTopic: string;
    sessions: number;
    lastActive: string;
  };

  const activeStudents = useMemo<ActiveStudent[]>(() => {
    const users = courseAnalyticsData?.getCourseSessionAnalytics?.topActiveUsers;

    if (!users) return [];

    return users.map((user) => {
      const initials = user.name
        ?.split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();

      return {
        id: user.id,
        name: user.name,
        initials,
        topTopic: "-", // We don’t have this yet
        sessions: user.totalSessions,
        lastActive: "", // We can compute later if needed
      };
    });
  }, [courseAnalyticsData]);

  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdated((prev) => new Date(prev));
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const activeStudentColumns: Column<ActiveStudent>[] = [
    {
      key: "student",
      flex: 3,
      renderCell: (student) => (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
          {/* Avatar */}
          <View
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: "#E0E7FF",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <Text style={{ color: "#3E68FE", fontWeight: "600" }}>{student.initials}</Text>
          </View>

          {/* Name + topic */}
          <View>
            <CellTextBold>{student.name}</CellTextBold>
            {/* do not delete the next line, we might use it later */}
            {/* <CellText style={{ color: "#667085" }}>Top topic: {student.topTopic}</CellText> */}
          </View>
        </View>
      ),
    },
    {
      key: "activity",
      flex: 1,
      textAlign: "right",
      alignItems: "flex-end",
      renderCell: (student) => (
        <View style={{ alignItems: "flex-end" }}>
          <CellTextBold>{student.sessions} sessions</CellTextBold>
          <CellText style={{ color: "#667085" }}>{student.lastActive}</CellText>
        </View>
      ),
    },
  ];
  const analytics = courseAnalyticsData?.getCourseSessionAnalytics;
  return (
    <AdminLayout withPadding={false}>
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.container}>
        {/* Page Header */}
        <MotiView
          from={{ opacity: 0, translateY: 20 }}
          animate={{ opacity: 1, translateY: 0 }}
          // @ts-expect-error - transition type not fully typed
          transition={{ type: "timing", duration: 500 }}
          style={styles.headerSection}>
          <View style={styles.headerContent}>
            <View>
              <View style={styles.titleRow}>
                <Text style={styles.title}>Dashboard</Text>
                <View style={styles.badge}>
                  <RobotIcon size={14} color={colors.purple[700]} weight="fill" />
                  <Text style={styles.badgeText}>AI Companion</Text>
                </View>
              </View>
              <Text style={styles.subtitle}>Monitor how your students are using AI Companion in your courses.</Text>
            </View>
            <View style={styles.headerActions}>
              <View style={styles.lastUpdated}>
                <Text style={styles.lastUpdatedLabel}>Last Updated</Text>
                <Text style={styles.lastUpdatedValue}>{formatTimeAgo(lastUpdated)}</Text>
              </View>
              <Pressable
                onPress={handleRefresh}
                disabled={isRefreshing}
                style={[styles.refreshButton, isRefreshing && styles.refreshButtonDisabled]}
                aria-label="Refresh data">
                <MotiView
                  animate={{ rotate: `${refreshRotation}deg` }}
                  transition={{
                    rotate: {
                      type: "timing",
                      duration: 1000,
                    },
                  }}>
                  <ArrowsClockwiseIcon size={20} color={colors.grey[600]} weight="regular" />
                </MotiView>
              </Pressable>
            </View>
          </View>
        </MotiView>

        {/* Stats Grid - 3 cards */}

        <FacultyStatsCards analytics={analytics} />

        {/* <Tabs value={selectedTab} onValueChange={handleTabChange}>
          <TabsList variant="soft" style={styles.tabsList}>
            <TabsTrigger variant="soft" value="common">
              <Text style={styles.tabTriggerText}>Common Questions</Text>
             
            </TabsTrigger>

            <TabsTrigger variant="soft" value="assignment">
              <Text style={styles.tabTriggerText}>Assignment Support</Text>
            </TabsTrigger>

            <TabsTrigger variant="soft" value="activity">
              <Text style={styles.tabTriggerText}>Student Activity</Text>
            </TabsTrigger>
          </TabsList>

          {/* Top Courses by Workspace Usage */}
        {/* <TabsContent value="activity">
            <View style={[styles.card, shadowStyles.sm]}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardTitle}>Most Active Students</Text>
                <Text style={styles.cardSubtitle}>This week</Text>
              </View>
              <DataTable columns={activeStudentColumns} data={activeStudents} keyExtractor={(item) => item.id} />
            </View>
          </TabsContent> */}

        {/* <TabsContent value="common">
            <View style={[styles.card, shadowStyles.sm]}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardTitle}>Common Questions</Text>

                <Text style={styles.cardSubtitle}>{commonQuestionCount} questions</Text>
              </View>

              <CommonQuestionsTab onCountChange={setCommonQuestionCount} />
            </View>
          </TabsContent> */}

        {/* <TabsContent value="assignment">
            <View style={[styles.card, shadowStyles.sm]}>
               <View style={styles.cardHeader}>
              <Text style={styles.cardTitle}>Assignment Support</Text>
              <Text style={styles.cardSubtitle}>{assignmentCount} assignments</Text>
               </View>
              <AssignmentSupportTab onCountChange={setAssignmentCount} />
            </View>
          </TabsContent> */}
        {/* </Tabs>  */}
      </ScrollView>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    backgroundColor: colors.grey[50],
  },
  container: {
    maxWidth: 1280,
    alignSelf: "center",
    width: "100%",
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
    // @ts-expect-error - minHeight works on web
    minHeight: "100vh",
    userSelect: "text",
  },
  tabTriggerText: {
    // marginBottom: spacing.lg,
  },
  headerSection: {
    marginBottom: spacing.xl,
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: spacing.xs,
  },
  title: {
    fontSize: typographyScale.displayMd.fontSize,
    lineHeight: typographyScale.displayMd.lineHeight,
    fontWeight: typographyStyles.fontBold.fontWeight,
    color: colors.grey[900],
  },
  badge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: spacing.xxs,
    backgroundColor: colors.purple[100],
    borderRadius: radii.full,
  },
  badgeText: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.purple[700],
  },
  subtitle: {
    fontSize: typographyScale.textBase.fontSize,
    lineHeight: typographyScale.textBase.lineHeight,
    color: colors.grey[600],
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  lastUpdated: {
    alignItems: "flex-end",
  },
  lastUpdatedLabel: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.grey[500],
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  lastUpdatedValue: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.grey[700],
  },
  refreshButton: {
    width: spacing["2xl"],
    height: spacing["2xl"],
    borderRadius: 20,
    backgroundColor: "rgba(255, 255, 255, 0.6)",
    borderWidth: 1,
    borderColor: colors.grey[200],
    alignItems: "center",
    justifyContent: "center",
  },
  refreshButtonDisabled: {
    opacity: 0.5,
  },
  statsGrid: {
    flexDirection: "row",
    gap: spacing.lg,
    marginBottom: spacing.xl,
  },
  tabsList: {
    marginBottom: spacing.lg,
  },
  tabContentSpacing: {
    gap: spacing.xl,
  },
  card: {
    backgroundColor: "rgba(255, 255, 255, 0.6)",
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.grey[200],
    padding: spacing.lg,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: spacing.lg,
  },
  cardTitle: {
    fontSize: typographyScale.textMd.fontSize,
    lineHeight: typographyScale.textMd.lineHeight,
    fontWeight: typographyStyles.fontSemibold.fontWeight,
    color: colors.grey[900],
  },
  cardSubtitle: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    color: colors.grey[500],
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
  },
});

export default FacultyDashboardCompanionView;
